// reconcileQueue.js — per-PersonId coalescing queue for Sync A.
//
// A save may fire reconcile while the previous reconcile for the same person is
// still in flight (a star move followed by a label edit). Two in flight would
// race on the same Exchange contact; stacking N would hammer the relay. So: at
// most ONE in flight per PersonId, and if any number of further requests land
// meanwhile, exactly ONE more run follows when it finishes — the last state of
// SharePoint is what the server reads, so one run after the last save covers
// every save before it. Different PersonIds never wait on each other.
//
// Pure: no React, no fetch. `run(personId)` is the caller's job; its rejection
// is swallowed here (the caller reports failure through its own state), so a
// failed run never wedges the queue.

export function createReconcileQueue(run) {
  const inflight = new Map();   // personId -> { again: boolean, done: Promise }

  function request(personId) {
    const cur = inflight.get(personId);
    if (cur) { cur.again = true; return cur.done; }
    const entry = { again: false, done: null };
    entry.done = (async () => {
      do {
        entry.again = false;
        try { await run(personId); } catch { /* reported by run's own state handling */ }
      } while (entry.again);
      inflight.delete(personId);
    })();
    inflight.set(personId, entry);
    return entry.done;
  }

  return {
    request,
    inFlight: personId => inflight.has(personId),
    size: () => inflight.size,
  };
}

// ── pill text ──
// One place that turns a reconcile outcome into the words on the pill, so the
// row head and the open panel can never disagree. Never includes contact data:
// field NAMES and reason codes only.

// Photo step reasons (people-exchange.js photoStep) in plain words. Anything
// else — timeout, download-failed, put-failed, delete-failed — is "didn't reach".
const PHOTO_REASON_TEXT = {
  missing: 'file not found in the library',
  'sharp-unavailable': 'photo processing unavailable',
};
export const photoReasonText = reason => PHOTO_REASON_TEXT[reason] || "photo didn't reach the phone";

export function pillFor(state) {
  if (!state) return null;
  switch (state.kind) {
    case 'pending': return { text: 'saved — phone copy pending', tone: 'pending', retry: false };
    case 'ok':      return { text: 'on phone ✓', tone: 'ok', retry: false };
    case 'photo-deferred': return { text: `photo not on phone yet — ${photoReasonText(state.reason)}`, tone: 'warn', retry: true };
    // held / phone-deleted open the resolve panel on tap (resolvePanel.js); a
    // plain retry would only reproduce the same state.
    case 'held':    return { text: 'held — changed on phone', tone: 'warn', retry: false, resolve: true };
    case 'phone-deleted': return { text: 'deleted on phone', tone: 'warn', retry: false, resolve: true };
    case 'stop':    return { text: `phone copy blocked: ${state.reason || 'stopped'}`, tone: 'warn', retry: true };
    // merged: this row was absorbed into another person (people-merge.js). Its
    // phone copy is gone by design, so there is nothing to retry or resolve —
    // no retry, no resolve key, muted tone. The detail view links the survivor.
    case 'merged':  return { text: 'Merged', tone: 'muted', retry: false };
    case 'failed':  return { text: 'phone copy failed — tap to retry', tone: 'error', retry: true };
    case 'dry':     return { text: `dry: would ${state.action}${state.fields?.length ? ' [' + state.fields.join(', ') + ']' : ''}${state.reason ? ' (' + state.reason + ')' : ''}`, tone: 'dry', retry: true };
    default:        return null;
  }
}

/** Reconcile result -> pill state. */
export function stateFromResult(r) {
  if (!r || typeof r !== 'object') return { kind: 'failed' };
  if (r.dryRun) return { kind: 'dry', action: r.action, fields: r.fields || [], reason: r.reason };
  switch (r.action) {
    case 'noop': case 'update': case 'create':
      // Fields landed; the photo may not have (Part 4). done/same/none are fine.
      if (r.photo && typeof r.photo === 'object' && r.photo.outcome === 'deferred') return { kind: 'photo-deferred', reason: r.photo.reason };
      return { kind: 'ok' };
    case 'held': return { kind: 'held', reason: r.reason };
    case 'stop': return { kind: 'stop', reason: r.reason };
    default: return { kind: 'failed' };
  }
}

/** A resolve result (contacts-resolve) -> pill state. newState is what the row holds now. */
export function stateFromResolve(r) {
  if (!r || typeof r !== 'object') return { kind: 'failed' };
  if (r.newState === 'ok' || r.newState === 'photo-deferred') return r.newState === 'photo-deferred' ? { kind: 'photo-deferred' } : { kind: 'ok' };
  if (r.newState === 'held') return { kind: 'held', reason: r.reason };
  if (r.newState === 'phone-deleted') return { kind: 'phone-deleted' };
  if (r.newState === 'stop') return { kind: 'stop', reason: r.reason };
  return { kind: 'failed' };
}

/**
 * People.ExchangeSyncState on load -> pill state, or null when nothing is
 * pending. photo-deferred carries no reason in the column, so the pill reads
 * "photo didn't reach the phone" with a Retry until the next live pass.
 */
export function stateFromColumn(exchangeSyncState) {
  if (exchangeSyncState === 'merged') return { kind: 'merged' };
  if (exchangeSyncState === 'held') return { kind: 'held' };
  if (exchangeSyncState === 'phone-deleted') return { kind: 'phone-deleted' };
  if (exchangeSyncState === 'stop') return { kind: 'stop' };
  if (exchangeSyncState === 'photo-deferred') return { kind: 'photo-deferred' };
  return null;
}
